import React, { useState } from 'react';
import Form from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import ObjectID from 'bson-objectid';
import {
  Button,
  InputLabel,
  Select,
  Grid,
  FormControl,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';

import { ADJUSTMENT_TYPES_MAP, Labels, ROUNDING_OPTIONS } from '../constants';
import NumericInput from 'components/shared/inputs/NumericInput';
import { ValueConclusion } from 'types/underwriting/underwriting';
import { adjustmentTypeOptionsForValueType } from '../helpers';
import { formatCurrencyFloat } from 'shared/utils/numberFormatters';
import { RentLossType } from '../types';

const roundingOptionsKeyValuePairs = ROUNDING_OPTIONS.map((value) => ({
  label: formatCurrencyFloat(value),
  value,
}));

type AdjustmentsAndLossesToolbarProps = {
  form: Form;
  hasResidentialUnits: boolean;
  hasCommercialUnits: boolean;
  valueConclusions: ValueConclusion[];
  valueAsComplete: boolean;
  valueAsStabilized: boolean;

  openRentLossModal: (type: RentLossType) => void;
};

const AdjustmentsAndLossesToolbar = observer(
  ({
    form,
    hasResidentialUnits,
    hasCommercialUnits,
    valueConclusions,
    valueAsComplete,
    valueAsStabilized,
    openRentLossModal,
  }: AdjustmentsAndLossesToolbarProps) => {
    const adjustmentTypeOptions = adjustmentTypeOptionsForValueType(valueConclusions);

    const [adjustmentType, setAdjustmentType] = useState<string>(adjustmentTypeOptions[0].value);

    const addAdjustment = () => {
      const targetField = ADJUSTMENT_TYPES_MAP[adjustmentType];
      if (!targetField) {
        return;
      }

      try {
        form.$(`incomeApproach.capRateConclusion.${targetField}`).add([
          {
            id: new ObjectID().toHexString(),
            name: '',
            amount: 0,
          },
        ]);
      } catch (error) {
        console.error('Error adding adjustment', error);
      }
    };

    const disableResidentialRentLossModal = (!valueAsStabilized && !valueAsComplete) || !hasResidentialUnits;
    const disableCommercialRentLossModal = (!valueAsStabilized && !valueAsComplete) || !hasCommercialUnits;

    return (
      <Grid container>
        <Grid item sx={{ minWidth: '505px', flexGrow: 1 }}>
          <FormControl size="small" fullWidth sx={{ marginBottom: '16px' }}>
            <InputLabel id="adjustmentType-select-label">Adjustment Type</InputLabel>
            <Select
              labelId="adjustmentType-select-label"
              label="Adjustment Type"
              size="small"
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value)}
            >
              {adjustmentTypeOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label || value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" gap={2} flexDirection="row">
            {valueAsComplete && (
              <NumericInput
                {...form.$('incomeApproach.capRateConclusion.asCompleteMonthsOfRentLoss').bind()}
                size="small"
                allowNegative={false}
                fullWidth
                label="Months of Rent Loss for As Complete Date"
              />
            )}
            {valueAsStabilized && (
              <NumericInput
                {...form.$('incomeApproach.capRateConclusion.asStabilizedMonthsOfRentLoss').bind()}
                size="small"
                allowNegative={false}
                fullWidth
                label="Months of Rent Loss for As Stabilized Date"
              />
            )}
            <FormControl size="small" fullWidth>
              <InputLabel id="roundingOptions-select-label">Round to Nearest</InputLabel>
              <Select
                {...form.$('incomeApproach.capRateConclusion.roundingFactor').bind()}
                labelId="roundingOptions-select-label"
                label="Round to Nearest"
                size="small"
              >
                {roundingOptionsKeyValuePairs.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label || value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item sx={{ width: '275px', marginLeft: '16px' }} display="flex" flexDirection="column" gap={1}>
          <Button variant="outlined" onClick={addAdjustment} disabled={!adjustmentType}>
            {Labels.ADD_ADJUSTMENT}
          </Button>
          <Button
            disabled={disableResidentialRentLossModal}
            onClick={() => openRentLossModal('residential')}
            variant="outlined"
          >
            {Labels.ADD_RESIDENTIAL_RENT_LOSS}
          </Button>
          <Button
            disabled={disableCommercialRentLossModal}
            onClick={() => openRentLossModal('commercial')}
            variant="outlined"
          >
            {Labels.ADD_COMMERCIAL_RENT_LOSS}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Grid container direction="column" sx={{ maxWidth: '315px' }}>
            <TextField
              {...form.$('incomeApproach.capRateConclusion.dateOfValuation').bind()}
              size="small"
              margin="normal"
              type="date"
              fullWidth
              label="As Is Date of Value"
              placeholder="As Is Date of Value"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0],
              }}
            />
            {valueAsStabilized && (
              <FormControlLabel
                control={
                  <Checkbox {...form.$('incomeApproach.capRateConclusion.includeAsStabilizedDiscountRate').bind()} />
                }
                label="Include As Stabilized Discount Rate?"
              />
            )}
            {valueAsComplete && (
              <FormControlLabel
                control={
                  <Checkbox {...form.$('incomeApproach.capRateConclusion.includeAsCompleteDiscountRate').bind()} />
                }
                label="Include As Complete Discount Rate?"
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default AdjustmentsAndLossesToolbar;
